import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { throwError } from 'rxjs';
import { HttpCancelService } from './http-cancel.service';
import { NotificationService } from './notification.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ConfigService } from '@app/core/config/config.service';
import { FileDownloadService } from './filedownload.service';
import { ApiError } from '../models/api-error.model';
import { AuthService } from '../guards/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AppHttpInterceptor implements HttpInterceptor {
  concurrentCalls: HttpRequest<any>[] = [];

  constructor(
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService,
    private httpCancelService: HttpCancelService,
    private configService: ConfigService,
    private fileDownloadService: FileDownloadService,
    private ngxService: NgxUiLoaderService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let requestId: string = null;
    if (req.reportProgress === true) {
      requestId = this.getRequestId(req.url);
    }

    if (this.isNgxUrl(req.url)) {
      this.ngxService.start();
      this.concurrentCalls.push(req);
    }

    if (this.authService.IsTokenExpired) {
      this.authService.forceRefresh();
    }

    return next.handle(req).pipe(
      takeUntil(this.httpCancelService.onCancelPendingRequests$),
      tap((event: any) => {
        if (event instanceof HttpResponse) {
          this.handleHttpResponse(req, event);
          return;
        }
        if (event.type === 3) {
          this.fileDownloadService.reportProgress(
            requestId,
            event.loaded,
            event.total
          );
        }
      }),
      catchError((httpErrorResponse) =>
        this.handleAuthError(httpErrorResponse, req)
      )
    );
  }

  private handleHttpResponse(
    req: HttpRequest<any>,
    response: HttpResponse<any>
  ) {
    this.handleNgxService(req);

    if (response.status === 206) {
      this.notificationService.warning(
        'Attention',
        "La requête n'a pas pu être traitée intégralement."
      );
      return;
    }

    if (req.method.toLowerCase() === 'get' || req.url.includes('datawell')) {
      return;
    }

    if (!this.shouldHideToastr(req.url)) {
      this.notificationService.success(
        'Succès',
        'Les informations ont été enregistrées avec succès.'
      );
    }
  }

  private handleAuthError(
    err: HttpErrorResponse,
    req: HttpRequest<any>
  ): Observable<any> {
    this.handleNgxService(req);

    const method = req.method;
    const url = req.url;
    if (
      method.toLowerCase() === 'get' &&
      (err.status === 401 || err.status === 403) &&
      this.isRedirectionUrl(url)
    ) {
      if (this.authService.IsTokenExpired) {
        this.authService.forceRefresh();
      }
      this.router.navigate([`/unauthorized`]);
      return of(err.message);
    } else if (
      (err.status === 400 || err.status === 403 || err.status === 404) &&
      !_.isNil(err.error?.Code)
    ) {
      const error: ApiError = err.error;
      this.notificationService.error(`Erreur ${error.Code}`, error.Description);
    } else if (method.toLowerCase() !== 'get') {
      this.notificationService.error('Erreur', 'Une erreur est survenue !');
    }
    return throwError(err);
  }

  private isNgxUrl(url: string): boolean {
    return (
      (_.isNil(this.configService.environnement) ||
        !this.configService.environnement.noNgxUrls.some((noNgxUrl) =>
          url.toLowerCase().includes(noNgxUrl.toLowerCase())
        )) &&
      !url.includes('datawell')
    );
  }

  private shouldHideToastr(url: string): boolean {
    return (
      !_.isNil(this.configService.environnement) &&
      this.configService.environnement.hideToastrUrls.some((u) =>
        url.toLowerCase().includes(u.toLowerCase())
      )
    );
  }

  private isRedirectionUrl(url: string): boolean {
    return (
      _.isNil(this.configService.environnement) ||
      !this.configService.environnement.noForbiddenRedirectionUrls.some(
        (noForbiddenRedirectionUrl) =>
          url.toLowerCase().includes(noForbiddenRedirectionUrl.toLowerCase())
      )
    );
  }

  private getRequestId(url: string): string {
    return _.last(url.split('/'));
  }

  private handleNgxService(req: HttpRequest<any>) {
    if (!this.isNgxUrl(req.url)) {
      return;
    }

    this.concurrentCalls = this.concurrentCalls.filter((r) => r != req);
    if (this.concurrentCalls.length == 0) {
      this.ngxService.stop();
    }
  }
}
